import { lazy, Suspense } from "react";
import { Navigate, Outlet, Route, Routes } from "react-router-dom";
import { AuthProvider, useAuth } from "./auth-context";
import { ToastProvider } from "./components/Toast";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { ImpersonationBanner } from "./components/ImpersonationBanner";
import { CookieBanner } from "./components/CookieBanner";
import { ThemeProvider } from "./theme-context";
import { I18nProvider } from "./i18n";

// Login is on the first paint for logged-out users — keep it eager so there
// is no splash-to-splash flicker. Everything else route-splits so the first
// bundle stays small for mobile.
import LoginPage from "./pages/Login";

const SignupPage = lazy(() => import("./pages/Signup"));
const ChoosePlan = lazy(() => import("./pages/ChoosePlan"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const VerifyEmail = lazy(() => import("./pages/VerifyEmail"));
const AcceptInvitationPage = lazy(() => import("./pages/AcceptInvitation"));
const ClubJoin = lazy(() => import("./pages/ClubJoin"));
const AccountPage = lazy(() => import("./pages/Account"));
const HelpPage = lazy(() => import("./pages/Help"));

const AthleteLayout = lazy(() => import("./pages/athlete/Layout"));
const AthleteHome = lazy(() => import("./pages/athlete/Home"));
const AthleteHistory = lazy(() => import("./pages/athlete/History"));
const AthleteZones = lazy(() => import("./pages/athlete/Zones"));
const AthleteMessages = lazy(() => import("./pages/athlete/Messages"));
const AthleteOnboarding = lazy(() => import("./pages/athlete/Onboarding"));
const AthleteAssessment = lazy(() => import("./pages/athlete/Assessment"));
const AthleteGeneratePlan = lazy(() => import("./pages/athlete/GeneratePlan"));

const CoachLayout = lazy(() => import("./pages/coach/Layout"));
const CoachHome = lazy(() => import("./pages/coach/Home"));
const CoachAthletes = lazy(() => import("./pages/coach/Athletes"));
const CoachAthleteDetail = lazy(() => import("./pages/coach/AthleteDetail"));
const CoachGeneratePlan = lazy(() => import("./pages/coach/GenerateForAthlete"));
const CoachAthletePlanDetail = lazy(() => import("./pages/coach/AthletePlanDetail"));
const CoachMessages = lazy(() => import("./pages/coach/Messages"));
const AdminLayout = lazy(() => import("./pages/coach/AdminUsers"));
const AdminUsersPage = lazy(() =>
  import("./pages/coach/AdminUsers").then((m) => ({ default: m.AdminUsersPage })),
);
const AdminWaitlistPage = lazy(() =>
  import("./pages/coach/AdminUsers").then((m) => ({ default: m.AdminWaitlistPage })),
);
const AdminUserDetail = lazy(() => import("./pages/coach/AdminUserDetail"));
const CoachClubs = lazy(() => import("./pages/coach/Clubs"));
const AdminOverview = lazy(() => import("./pages/coach/admin/Overview"));
const AdminBilling = lazy(() => import("./pages/coach/admin/Billing"));
const AdminAudit = lazy(() => import("./pages/coach/admin/Audit"));
const AdminAnalytics = lazy(() => import("./pages/coach/admin/Analytics"));
const AdminOps = lazy(() => import("./pages/coach/admin/Ops"));
const AdminTeam = lazy(() => import("./pages/coach/admin/Team"));

export default function App() {
  return (
    <ErrorBoundary>
     <ThemeProvider>
      <I18nProvider>
       <AuthProvider>
        <ToastProvider>
          <a href="#main" className="a11y-skip">Skip to main content</a>
          <ImpersonationBanner />
          <CookieBanner />
          <Suspense fallback={<FullScreenLoader />}>
            <Routes>
              <Route path="/login" element={<LoginPage />} />
              <Route path="/signup" element={<SignupPage />} />
              <Route path="/choose-plan" element={<ChoosePlan />} />
              <Route path="/forgot" element={<ForgotPassword />} />
              <Route path="/reset/:token" element={<ResetPassword />} />
              <Route path="/verify-email/:token" element={<VerifyEmail />} />
              <Route path="/accept/:token" element={<AcceptInvitationPage />} />
              <Route path="/clubs/join/:token" element={<ClubJoin />} />
              <Route path="/account" element={<AccountGate />} />
              <Route path="/help" element={<HelpGate />} />

              <Route element={<RequireAuth role="athlete" />}>
                <Route path="/athlete" element={<AthleteLayout />}>
                  <Route index element={<AthleteHome />} />
                  <Route path="history" element={<AthleteHistory />} />
                  <Route path="zones" element={<AthleteZones />} />
                  <Route path="messages" element={<AthleteMessages />} />
                  <Route path="messages/:otherId" element={<AthleteMessages />} />
                  <Route path="onboarding" element={<AthleteOnboarding />} />
                  <Route path="assessment" element={<AthleteAssessment />} />
                <Route path="plan/new" element={<AthleteGeneratePlan />} />
                </Route>
              </Route>

              {/* Legacy /app/* -> /athlete/* redirect so old bookmarks keep working. */}
              <Route path="/app" element={<Navigate to="/athlete" replace />} />
              <Route path="/app/*" element={<LegacyAppRedirect />} />

              <Route element={<RequireAuth role="coach" />}>
                <Route path="/coach" element={<CoachLayout />}>
                  <Route index element={<CoachHome />} />
                  <Route path="athletes" element={<CoachAthletes />} />
                  <Route path="athletes/:id" element={<CoachAthleteDetail />} />
                  <Route path="athletes/:id/plans/new" element={<CoachGeneratePlan />} />
                  <Route path="athletes/:id/plans/:planId" element={<CoachAthletePlanDetail />} />
                  <Route path="messages" element={<CoachMessages />} />
                  <Route path="messages/:otherId" element={<CoachMessages />} />
                  <Route path="admin" element={<AdminLayout />}>
                    <Route index element={<AdminOverview />} />
                    <Route path="users" element={<AdminUsersPage />} />
                    <Route path="waitlist" element={<AdminWaitlistPage />} />
                    <Route path="clubs" element={<CoachClubs />} />
                    <Route path="clubs/:id" element={<CoachClubs />} />
                    <Route path="billing" element={<AdminBilling />} />
                    <Route path="analytics" element={<AdminAnalytics />} />
                    <Route path="ops" element={<AdminOps />} />
                    <Route path="audit" element={<AdminAudit />} />
                    <Route path="team" element={<AdminTeam />} />
                  </Route>
                  <Route path="admin/users/:id" element={<AdminUserDetail />} />
                  {/* Legacy /coach/clubs path — clubs are admin-only now. */}
                  <Route path="clubs" element={<Navigate to="/coach/admin/clubs" replace />} />
                  <Route path="clubs/:id" element={<LegacyClubsRedirect />} />
                </Route>
              </Route>

              <Route path="*" element={<RootRedirect />} />
            </Routes>
          </Suspense>
        </ToastProvider>
       </AuthProvider>
      </I18nProvider>
     </ThemeProvider>
    </ErrorBoundary>
  );
}

function RootRedirect() {
  const { user, loading } = useAuth();
  if (loading) return <FullScreenLoader />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === "coach" || user.role === "admin") return <Navigate to="/coach" replace />;
  return <Navigate to="/athlete" replace />;
}

/** Preserve any path beyond /app and redirect it to the matching /athlete path. */
function LegacyAppRedirect() {
  const rest = window.location.pathname.replace(/^\/app/, "");
  return <Navigate to={`/athlete${rest}${window.location.search}`} replace />;
}

/** /coach/clubs/:id → /coach/admin/clubs/:id. Clubs moved under admin. */
function LegacyClubsRedirect() {
  const rest = window.location.pathname.replace(/^\/coach\/clubs/, "");
  return <Navigate to={`/coach/admin/clubs${rest}${window.location.search}`} replace />;
}

function RequireAuth({ role }: { role: "athlete" | "coach" }) {
  const { user, loading } = useAuth();
  if (loading) return <FullScreenLoader />;
  if (!user) return <Navigate to="/login" replace />;
  const isCoach = user.role === "coach" || user.role === "admin";
  if (role === "coach" && !isCoach) return <Navigate to="/athlete" replace />;
  if (role === "athlete" && isCoach) return <Navigate to="/coach" replace />;
  return <Outlet />;
}

function AccountGate() {
  const { user, loading } = useAuth();
  if (loading) return <FullScreenLoader />;
  if (!user) return <Navigate to="/login" replace />;
  return <AccountPage />;
}

function HelpGate() {
  const { user, loading } = useAuth();
  if (loading) return <FullScreenLoader />;
  if (!user) return <Navigate to="/login" replace />;
  return <HelpPage />;
}

function FullScreenLoader() {
  return (
    <div className="min-h-dvh flex items-center justify-center">
      <div className="flex flex-col items-center gap-3 text-px-text-muted">
        <div className="size-10 rounded-full border-2 border-px-line border-t-px-green animate-spin" />
        <p className="text-xs uppercase tracking-widest">Loading</p>
      </div>
    </div>
  );
}
